/**
 * Risultato e tabellino.
 *
 * Il punteggio non e memorizzato da nessuna parte: e il conteggio dei gol non
 * cancellati. Cancellare un gol lo aggiorna da solo, senza alcuna colonna da
 * tenere sincronizzata.
 *
 * Queste funzioni hanno un gemello SQL in `apps/api/src/servizi/calcoli-sql.ts`.
 * Il client le usa per aggiornare il punteggio al tocco, senza attendere il
 * server; il server usa le espressioni SQL per le letture aggregate su piu
 * partite. Le due implementazioni devono restare allineate: il test
 * `statistiche-rotte` le confronta sugli stessi eventi.
 */

import type { Evento, Formato, TipoEvento } from './formato.js';
import { eventiAttivi, formattaMinuto } from './formato.js';
import { ordinaEventi } from './cronometro.js';
import { ricalcolaPermanenze } from './partecipazioni.js';

export interface Risultato {
  golFatti: number;
  golSubiti: number;
}

/**
 * L'autogol di un nostro giocatore e una rete avversaria, ed e alternativo
 * all'evento gol_subito: registrarli entrambi per la stessa rete
 * raddoppierebbe il punteggio. Nelle statistiche individuali resta invece
 * contato a parte e mai sommato ai gol realizzati. L'autogol di un avversario
 * e invece una rete nostra che non ha un marcatore.
 */
export function calcolaRisultato(eventi: readonly Evento[]): Risultato {
  let golFatti = 0;
  let golSubiti = 0;
  for (const e of eventiAttivi(eventi)) {
    if (e.tipo === 'gol' || e.tipo === 'autogol_avversario') golFatti++;
    else if (e.tipo === 'gol_subito' || e.tipo === 'autogol') golSubiti++;
  }
  return { golFatti, golSubiti };
}

export interface StatisticheGiocatore {
  giocatoreId: string;
  gol: number;
  autogol: number;
  assist: number;
  tiriSpecchio: number;
  tiriFuori: number;
  passaggiCompletati: number;
  passaggiSbagliati: number;
  palleConquistate: number;
  pallePerse: number;
  gialli: number;
  rossi: number;
  minuti: number;
  titolare: boolean;
  espulso: boolean;
  /** Null quando non ci sono tentativi: zero su zero non e zero per cento. */
  precisionePassaggi: number | null;
  precisioneTiri: number | null;
}

const vuote = (giocatoreId: string): StatisticheGiocatore => ({
  giocatoreId,
  gol: 0,
  autogol: 0,
  assist: 0,
  tiriSpecchio: 0,
  tiriFuori: 0,
  passaggiCompletati: 0,
  passaggiSbagliati: 0,
  palleConquistate: 0,
  pallePerse: 0,
  gialli: 0,
  rossi: 0,
  minuti: 0,
  titolare: false,
  espulso: false,
  precisionePassaggi: null,
  precisioneTiri: null,
});

const CONTATORI: Partial<Record<TipoEvento, keyof StatisticheGiocatore>> = {
  gol: 'gol',
  autogol: 'autogol',
  assist: 'assist',
  tiro_specchio: 'tiriSpecchio',
  tiro_fuori: 'tiriFuori',
  passaggio_completato: 'passaggiCompletati',
  passaggio_sbagliato: 'passaggiSbagliati',
  palla_conquistata: 'palleConquistate',
  palla_persa: 'pallePerse',
  cartellino_giallo: 'gialli',
  cartellino_rosso: 'rossi',
};

const percentuale = (parte: number, totale: number): number | null =>
  totale === 0 ? null : Math.round((parte / totale) * 1000) / 10;

/** Una voce del tabellino: il racconto della partita in ordine di minuto. */
export interface VoceTabellino {
  eventoId: string;
  tipo: TipoEvento | 'sostituzione';
  periodo: number;
  minuto: number;
  etichetta: string;
  giocatoreId: string | null;
  /** Valorizzato solo per le sostituzioni: chi esce. */
  giocatoreUscenteId?: string;
  /** Punteggio subito dopo l'azione, solo sulle voci che lo cambiano. */
  risultatoDopo?: Risultato;
}

export interface Tabellino {
  risultato: Risultato;
  statistiche: StatisticheGiocatore[];
  voci: VoceTabellino[];
  /** Id dei marcatori in ordine di minuto, per l'intestazione della partita. */
  marcatori: { giocatoreId: string; minuto: number; etichetta: string }[];
}

/**
 * @param minutoFinale minuto effettivo a cui si contano i minuti di chi e
 *   ancora in campo: `minutoEffettivo` del cronometro.
 */
export function calcolaTabellino(
  f: Formato,
  eventi: readonly Evento[],
  minutoFinale: number,
): Tabellino {
  const attivi = ordinaEventi(eventiAttivi(eventi));
  const { minuti, espulsi, permanenze } = ricalcolaPermanenze(f, attivi, minutoFinale);

  // --- Statistiche individuali ----------------------------------------------
  const per = new Map<string, StatisticheGiocatore>();
  const prendi = (g: string): StatisticheGiocatore => {
    let s = per.get(g);
    if (!s) {
      s = vuote(g);
      per.set(g, s);
    }
    return s;
  };

  for (const e of attivi) {
    if (!e.giocatoreId) continue;
    const campo = CONTATORI[e.tipo];
    if (!campo) continue;
    const s = prendi(e.giocatoreId);
    (s[campo] as number)++;
  }

  for (const [g, m] of minuti) prendi(g).minuti = m;
  for (const p of permanenze) if (p.titolare) prendi(p.giocatoreId).titolare = true;
  for (const g of espulsi) prendi(g).espulso = true;

  for (const s of per.values()) {
    s.precisionePassaggi = percentuale(
      s.passaggiCompletati,
      s.passaggiCompletati + s.passaggiSbagliati,
    );
    s.precisioneTiri = percentuale(s.tiriSpecchio, s.tiriSpecchio + s.tiriFuori);
  }

  // --- Racconto della partita ------------------------------------------------
  // Uscite e ingressi allo stesso minuto si presentano come una sostituzione
  // sola, che e il modo in cui un tabellino si legge.
  const voci: VoceTabellino[] = [];
  const marcatori: Tabellino['marcatori'] = [];
  const ingressiUsati = new Set<string>();
  const progressivo: Risultato = { golFatti: 0, golSubiti: 0 };

  for (const e of attivi) {
    if (e.tipo === 'uscita') {
      const abbinato = attivi.find(
        (x) =>
          x.tipo === 'ingresso' &&
          x.periodo === e.periodo &&
          x.minuto === e.minuto &&
          x.minuto > 0 &&
          !ingressiUsati.has(x.id),
      );
      if (abbinato) ingressiUsati.add(abbinato.id);
      voci.push({
        eventoId: e.id,
        tipo: 'sostituzione',
        periodo: e.periodo,
        minuto: e.minuto,
        etichetta: formattaMinuto(e.minuto, e.periodo, f),
        giocatoreId: abbinato?.giocatoreId ?? null,
        giocatoreUscenteId: e.giocatoreId ?? undefined,
      });
      continue;
    }

    // Un ingresso senza uscita abbinata resta una voce a se: succede quando
    // la squadra rientra in parita numerica o nei formati a rientro libero.
    if (e.tipo === 'ingresso' && e.minuto > 0 && !ingressiUsati.has(e.id)) {
      voci.push({
        eventoId: e.id,
        tipo: 'sostituzione',
        periodo: e.periodo,
        minuto: e.minuto,
        etichetta: formattaMinuto(e.minuto, e.periodo, f),
        giocatoreId: e.giocatoreId,
      });
      continue;
    }

    // Gli eventi di periodo si comandano solo dal cronometro, mai dal
    // cestino dell'elenco: non compaiono qui, la macchina a stati resta
    // leggibile dai soli comandi inizio/fine/sospendi/riprendi.
    if (e.tipo === 'inizio_periodo' || e.tipo === 'fine_periodo') continue;

    // Ogni ingresso rimasto a questo punto o e un titolare al fischio
    // d'inizio o e gia stato assorbito in una sostituzione sopra: mostrarlo
    // anche qui lo duplicherebbe nel racconto della partita.
    if (e.tipo === 'ingresso') continue;

    const voce: VoceTabellino = {
      eventoId: e.id,
      tipo: e.tipo,
      periodo: e.periodo,
      minuto: e.minuto,
      etichetta: formattaMinuto(e.minuto, e.periodo, f),
      giocatoreId: e.giocatoreId,
    };

    if (e.tipo === 'gol') {
      progressivo.golFatti++;
      voce.risultatoDopo = { ...progressivo };
      if (e.giocatoreId) {
        marcatori.push({
          giocatoreId: e.giocatoreId,
          minuto: e.minuto,
          etichetta: voce.etichetta,
        });
      }
    } else if (e.tipo === 'autogol_avversario') {
      progressivo.golFatti++;
      voce.risultatoDopo = { ...progressivo };
    } else if (e.tipo === 'gol_subito' || e.tipo === 'autogol') {
      progressivo.golSubiti++;
      voce.risultatoDopo = { ...progressivo };
    }

    voci.push(voce);
  }

  return {
    risultato: { ...progressivo },
    statistiche: [...per.values()].sort((a, b) => b.minuti - a.minuti),
    voci,
    marcatori,
  };
}
